import parseQueryStringCaseInsensitive from "@insite/client-framework/Common/Utilities/parseQueryStringCaseInsensitive";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconWrapper } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import Info from "@WhiteCap/Icons/Info";
import CustomApplicationState from "@WhiteCap/Store/Data/CustomApplicationState/CustomApplicationState";
import { getOrderDetailsState } from "@WhiteCap/Store/Reducer/WhiteCapReducer/OrderDeatilsCustom/OrderDetailsCustomSelector";
import React from "react";
import { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomApplicationState) => {
    const location = getLocation(state);
    let orderNumber = "";
    if (location?.search) {
        const parsedQuery = parseQueryStringCaseInsensitive(location.search, { orderNumber: "" });
        orderNumber = parsedQuery.orderNumber || "";
    }

    const returnOrders = orderNumber ? state.WhiteCap.returnOrderStore.byOrderId[orderNumber]?.data || [] : [];
    const orderState = getOrderDetailsState(state, orderNumber);
    const orderDetailsPage = getPageLinkByPageType(state, "OrderDetailsPage");

    return {
        returnOrders,
        orderState,
        orderDetailsPage,
    };
};

export interface ReturnOrdersStyles {
    container?: GridContainerProps;
    gridItem?: GridItemProps;
    warning?: TypographyProps;
    returnLink?: LinkPresentationProps;
}

const returnOrdersStyles: ReturnOrdersStyles = {
    container: {
        css: css`
            width: 100%;
            border: 1px solid #428bf8;
            padding: 8px 16px;
            margin-bottom: 35px !important;
        `,
    },
    gridItem: {
        css: css`
            width: 100%;
            flex-basis: 100%;
            max-width: 100%;
            > ${IconWrapper} {
                width: 16px;
                height: 20px;
                margin-right: 10px;
            }
        `,
    },
    warning: {
        size: 16,
        css: css`
            line-height: 24px;
        `,
    },
    returnLink: {
        css: css`
            color: #2e64b0;
            font-weight: 500;
        `,
    },
};

const styles = returnOrdersStyles;

type Props = ReturnType<typeof mapStateToProps> & HasHistory;

const ReturnOrdersInfoContainer: FC<Props> = ({ returnOrders, orderState, orderDetailsPage, history }) => {
    const order = orderState.value;
    const properties = order?.properties || {};
    const isReturnOrder = properties.isReturnOrder === "True";

    const redirectToReturnOrderSection = () => {
        const element = document.getElementById("return-orders-section");

        if (element) {
            const offsetFromTop = 0;

            const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
            const offsetPosition = elementPosition - offsetFromTop;

            window.scrollTo({
                top: offsetPosition,
                behavior: "smooth",
            });
        }
    };

    const parentOrderLink = () => {
        if (properties.parentOrderNumber && orderDetailsPage?.url) {
            const normalizedParentOrderNumber = properties.parentOrderNumber.split(";")[0];
            history.push(`${orderDetailsPage.url}?orderNumber=${normalizedParentOrderNumber}`);
        }
    };

    const shouldShowBanner = returnOrders.length > 0 || isReturnOrder;

    if (!shouldShowBanner) {
        return null;
    }

    return (
        <GridContainer {...styles.container}>
            <GridItem {...styles.gridItem}>
                <Icon src={Info} />
                {returnOrders.length > 0 ? (
                    <Typography {...styles.warning}>
                        {translate("Return-Order-ItemsReturned")}
                        <Link {...styles.returnLink} onClick={redirectToReturnOrderSection}>
                            &nbsp;{translate("Return-Order-ViewReturnedItems")}
                        </Link>
                        <br />
                        {translate("Return-Order-ContactMessage")}
                    </Typography>
                ) : isReturnOrder ? (
                    <Typography {...styles.warning}>
                        {translate("Parent-Order-ThisIsReturnOrder")}
                        <Link {...styles.returnLink} onClick={parentOrderLink}>
                            &nbsp;{translate("Parent-Order-ViewOriginalOrder")}
                        </Link>
                        <br />
                        {translate("Parent-Order-ContactMessage")}
                    </Typography>
                ) : null}
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withHistory(ReturnOrdersInfoContainer)),
    definition: {
        allowedContexts: ["OrderDetailsPage", "VmiOrderDetailsPage", "CustomOrderStatusPage"],
        group: "Order Details",
        displayName: "Return Orders Info Container",
    },
};

export default widgetModule;
